import { ServerTokens } from 'server/server-tokens.enum';

import { Inject, Injectable, Optional, makeStateKey, TransferState } from '@angular/core';
import { Translations } from '@core/services/translate-loader.service';
import { environment } from '@environments/environment';
import {
    CmsSettingsViewModel,
    FooterMenuViewModel,
    MainMenuViewModel,
    MetaMenuLinkItemViewModel,
    PageViewModel
} from '@models/api.models';
import { BreakpointService } from '@libs/breakpoints';

import { ServerEnvironments } from '../../../../server/server-environments.enum';

@Injectable({
    providedIn: 'root'
})
export class ServerTokensService {
    public readonly aiKey?: string;
    public readonly pageResponse?: PageViewModel;
    public readonly settingsResponse?: CmsSettingsViewModel;
    public readonly translationsResponse?: Translations;
    public readonly navigationResponse?: MainMenuViewModel;
    public readonly metaNavigationResponse?: MetaMenuLinkItemViewModel[];
    public readonly footerNavigationResponse?: FooterMenuViewModel;
    public readonly httpTimeoutSeconds: number;
    public readonly isBot?: boolean;
    public readonly logVerbose: boolean;
    public readonly sentryKey?: string;
    public readonly serverEnv: ServerEnvironments;
    public readonly ssrDisabled?: boolean;
    public readonly cmsCacheKey: string;
    public readonly enableAi: boolean;
    public readonly mapboxToken?: string;
    public readonly mapboxStyle?: string;
    public readonly loginRedirectPath?: string;
    public readonly loginClientId?: string;
    public readonly loginAuthorityProvider?: string;
    public readonly loginSignUpSignInFlow?: string;
    public readonly loginSignUpFlow?: string;
    public readonly loginResetPasswordFlow?: string;

    constructor(
        transferState: TransferState,
        breakpoint: BreakpointService,
        @Optional()
        @Inject(ServerTokens.AiKey)
        aiKey: string,
        @Optional()
        @Inject(ServerTokens.CmsPage)
        pageResponse: PageViewModel,
        @Optional()
        @Inject(ServerTokens.CmsSettings)
        settingsResponse: CmsSettingsViewModel,
        @Optional()
        @Inject(ServerTokens.CmsTranslations)
        translationsResponse: Translations,
        @Optional()
        @Inject(ServerTokens.CmsMainMenu)
        navigationResponse: MainMenuViewModel,
        @Optional()
        @Inject(ServerTokens.CmsMetaMenu)
        metaNavigationResponse: MetaMenuLinkItemViewModel[],
        @Optional()
        @Inject(ServerTokens.CmsFooterMenu)
        footerNavigationResponse: FooterMenuViewModel,
        @Optional()
        @Inject(ServerTokens.HttpTimeoutSeconds)
        httpTimeoutSeconds: number,
        @Optional()
        @Inject(ServerTokens.IsBot)
        isBot: boolean,
        @Optional()
        @Inject(ServerTokens.LogVerbose)
        logVerbose: boolean,
        @Optional()
        @Inject(ServerTokens.SentryKeyBrowser)
        sentryKeyBrowser: string,
        @Optional()
        @Inject(ServerTokens.SentryKeySsr)
        sentryKeySsr: string,
        @Optional()
        @Inject(ServerTokens.ServerEnv)
        serverEnv: ServerEnvironments,
        @Optional()
        @Inject(ServerTokens.SsrDisabled)
        ssrDisabled: boolean,
        @Optional()
        @Inject(ServerTokens.CmsCacheKey)
        cmsCacheKey: string,
        @Optional()
        @Inject(ServerTokens.EnableAi)
        enableAi: boolean,
        @Optional()
        @Inject(ServerTokens.MapboxToken)
        mapboxToken: string,
        @Optional()
        @Inject(ServerTokens.MapboxStyle)
        mapboxStyle: string,
        @Optional()
        @Inject(ServerTokens.LoginRedirectPath)
        loginRedirectPath: string,
        @Optional()
        @Inject(ServerTokens.LoginClientId)
        loginClientId: string,
        @Optional()
        @Inject(ServerTokens.LoginAuthorityProvider)
        loginAuthorityProvider: string,
        @Optional()
        @Inject(ServerTokens.LoginSignUpSignInFlow)
        loginSignUpSignInFlow: string,
        @Optional()
        @Inject(ServerTokens.LoginSignUpFlow)
        loginSignUpFlow: string,
        @Optional()
        @Inject(ServerTokens.LoginResetPasswordFlow)
        loginResetPasswordFlow: string
    ) {
        const stateAiKey = makeStateKey<string>('stateAiKey');
        const stateCmsPage = makeStateKey<PageViewModel>('stateCmsPage');
        const stateCmsSettings = makeStateKey<CmsSettingsViewModel>('stateCmsSettings');
        const stateCmsTranslations = makeStateKey<Translations>('stateCmsTranslations');
        const stateCmsNavigation = makeStateKey<MainMenuViewModel>('stateCmsNavigation');
        const stateCmsMetaNavigation = makeStateKey<MetaMenuLinkItemViewModel[]>('stateCmsMetaNavigation');
        const stateCmsFooterNavigation = makeStateKey<FooterMenuViewModel>('stateCmsFooterNavigation');
        const stateHttpTimeoutSeconds = makeStateKey<number>('stateHttpTimeoutSeconds');
        const stateIsBot = makeStateKey<boolean>('stateIsBot');
        const stateLogVerbose = makeStateKey<boolean>('stateLogVerbose');
        const stateSentryKeyBrowser = makeStateKey<string>('stateSentryKeyBrowser');
        const stateServerEnv = makeStateKey<ServerEnvironments>('stateServerEnv');
        const stateSsrDisabled = makeStateKey<boolean>('stateSsrDisabled');
        const stateCmsCacheKey = makeStateKey<string>('stateCmsCacheKey');
        const stateEnableAi = makeStateKey<boolean>('stateEnableAi');
        const stateMapboxToken = makeStateKey<string>('stateMapboxToken');
        const stateMapboxStyle = makeStateKey<string>('stateMapboxStyle');
        const stateLoginRedirectPath = makeStateKey<string>('stateLoginRedirectPath');
        const stateLoginClientId = makeStateKey<string>('stateLoginClientId');
        const stateLoginAuthorityProvider = makeStateKey<string>('stateLoginAuthorityProvider');
        const stateLoginSignUpSignInFlow = makeStateKey<string>('stateLoginSignUpSignInFlow');
        const stateLoginSignUpFlow = makeStateKey<string>('stateLoginSignUpFlow');
        const stateLoginResetPasswordFlow = makeStateKey<string>('stateLoginResetPasswordFlow');

        if (breakpoint.isServer()) {
            // Set values for SSR
            this.aiKey = aiKey;
            this.pageResponse = pageResponse;
            this.settingsResponse = settingsResponse;
            this.translationsResponse = translationsResponse;
            this.navigationResponse = navigationResponse;
            this.metaNavigationResponse = metaNavigationResponse;
            this.footerNavigationResponse = footerNavigationResponse;
            this.httpTimeoutSeconds = httpTimeoutSeconds ? httpTimeoutSeconds : 60;
            this.isBot = isBot;
            this.logVerbose = logVerbose || logVerbose === undefined || !environment.production;
            this.sentryKey = sentryKeySsr;
            this.serverEnv = serverEnv ? serverEnv : ServerEnvironments.Dev;
            this.ssrDisabled = ssrDisabled;
            this.cmsCacheKey = cmsCacheKey;
            this.enableAi = Boolean(enableAi && aiKey);
            this.cmsCacheKey = cmsCacheKey;
            this.mapboxToken = mapboxToken;
            this.mapboxStyle = mapboxStyle;
            this.loginRedirectPath = loginRedirectPath;
            this.loginClientId = loginClientId;
            this.loginAuthorityProvider = loginAuthorityProvider;
            this.loginSignUpSignInFlow = loginSignUpSignInFlow;
            this.loginSignUpFlow = loginSignUpFlow;
            this.loginResetPasswordFlow = loginResetPasswordFlow;

            // Store in transfer state for browser
            transferState.set(stateAiKey, this.aiKey);
            transferState.set(stateCmsPage, this.pageResponse);
            transferState.set(stateCmsSettings, this.settingsResponse);
            transferState.set(stateCmsTranslations, this.translationsResponse);
            transferState.set(stateCmsNavigation, this.navigationResponse);
            transferState.set(stateCmsMetaNavigation, this.metaNavigationResponse);
            transferState.set(stateCmsFooterNavigation, this.footerNavigationResponse);
            transferState.set(stateHttpTimeoutSeconds, this.httpTimeoutSeconds);
            transferState.set(stateIsBot, this.isBot);
            transferState.set(stateLogVerbose, this.logVerbose);
            transferState.set(stateSentryKeyBrowser, sentryKeyBrowser);
            transferState.set(stateServerEnv, this.serverEnv);
            transferState.set(stateSsrDisabled, this.ssrDisabled);
            transferState.set(stateCmsCacheKey, this.cmsCacheKey);
            transferState.set(stateEnableAi, this.enableAi);
            transferState.set(stateMapboxToken, this.mapboxToken);
            transferState.set(stateMapboxStyle, this.mapboxStyle);
            transferState.set(stateLoginRedirectPath, this.loginRedirectPath);
            transferState.set(stateLoginClientId, this.loginClientId);
            transferState.set(stateLoginAuthorityProvider, this.loginAuthorityProvider);
            transferState.set(stateLoginSignUpSignInFlow, this.loginSignUpSignInFlow);
            transferState.set(stateLoginSignUpFlow, this.loginSignUpFlow);
            transferState.set(stateLoginResetPasswordFlow, this.loginResetPasswordFlow);
        } else {
            // Get values from transfer state for browser
            this.aiKey = transferState.get(stateAiKey, undefined);
            this.pageResponse = transferState.get(stateCmsPage, undefined);
            this.settingsResponse = transferState.get(stateCmsSettings, undefined);
            this.translationsResponse = transferState.get(stateCmsTranslations, undefined);
            this.navigationResponse = transferState.get(stateCmsNavigation, undefined);
            this.metaNavigationResponse = transferState.get(stateCmsMetaNavigation, undefined);
            this.footerNavigationResponse = transferState.get(stateCmsFooterNavigation, undefined);
            this.httpTimeoutSeconds = transferState.get(stateHttpTimeoutSeconds, 60);
            this.isBot = transferState.get(stateIsBot, true);
            this.logVerbose = transferState.get(stateLogVerbose, !environment.production);
            this.sentryKey = transferState.get(stateSentryKeyBrowser, undefined);
            this.serverEnv = transferState.get(stateServerEnv, ServerEnvironments.Dev);
            this.ssrDisabled = transferState.get(stateSsrDisabled, true);
            this.cmsCacheKey = transferState.get(stateCmsCacheKey, String(Date.now()));
            this.enableAi = transferState.get(stateEnableAi, false);
            this.mapboxToken = transferState.get(stateMapboxToken, undefined);
            this.mapboxStyle = transferState.get(stateMapboxStyle, undefined);
            this.loginRedirectPath = transferState.get(stateLoginRedirectPath, undefined);
            this.loginClientId = transferState.get(stateLoginClientId, undefined);
            this.loginAuthorityProvider = transferState.get(stateLoginAuthorityProvider, undefined);
            this.loginSignUpSignInFlow = transferState.get(stateLoginSignUpSignInFlow, undefined);
            this.loginSignUpFlow = transferState.get(stateLoginSignUpFlow, undefined);
            this.loginResetPasswordFlow = transferState.get(stateLoginResetPasswordFlow, undefined);
        }
    }
}
